(() => {
  const header = document.querySelector('[data-header]');
  const toggle = document.querySelector('[data-menu-toggle]');
  if (header && toggle) {
    const closeMenu = (restoreFocus = false) => {
      header.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      toggle.setAttribute('aria-expanded', 'false');
      const label = toggle.querySelector('.visually-hidden');
      if (label) label.textContent = toggle.dataset.openLabel || 'Open menu';
      if (restoreFocus) toggle.focus();
    };

    toggle.addEventListener('click', () => {
      const open = header.classList.toggle('is-open');
      document.body.classList.toggle('menu-open', open);
      const label = toggle.querySelector('.visually-hidden');
      if (label) label.textContent = open ? (toggle.dataset.closeLabel || 'Close menu') : (toggle.dataset.openLabel || 'Open menu');
      toggle.setAttribute('aria-expanded', String(open));
      if (open) window.requestAnimationFrame(() => header.querySelector('#SiteNav a')?.focus());
    });

    header.querySelectorAll('#SiteNav a').forEach((link) => {
      link.addEventListener('click', () => closeMenu());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && header.classList.contains('is-open')) closeMenu(true);
      if (event.key === 'Tab' && header.classList.contains('is-open')) {
        const focusable = Array.from(header.querySelectorAll('button:not([disabled]), a[href]'));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    });

    window.addEventListener('resize', () => {
      if (window.matchMedia('(min-width: 990px)').matches) closeMenu();
    });

    const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 16);
    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });
  }

  document.querySelectorAll('[data-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });

  document.querySelectorAll('[data-facets-form]').forEach((form) => {
    const toggleButton = form.querySelector('[data-facets-toggle]');
    const panel = form.querySelector('[data-facets-panel]');
    toggleButton?.addEventListener('click', () => {
      const open = panel?.classList.toggle('is-open') || false;
      toggleButton.setAttribute('aria-expanded', String(open));
    });
    form.querySelector('[data-facets-sort]')?.addEventListener('change', () => form.submit());
  });

  const searchDialog = document.querySelector('[data-search-dialog]');
  if (searchDialog) {
    const searchInput = searchDialog.querySelector('[data-predictive-search-input]');
    const searchResults = searchDialog.querySelector('[data-predictive-search-results]');
    let searchController;
    let searchTimer;
    document.querySelectorAll('[data-search-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (header?.classList.contains('is-open')) closeMenu();
        searchDialog.showModal();
        window.requestAnimationFrame(() => searchInput?.focus());
      });
    });
    searchDialog.querySelector('[data-search-close]')?.addEventListener('click', () => searchDialog.close());
    searchDialog.addEventListener('click', (event) => {
      if (event.target === searchDialog) searchDialog.close();
    });
    searchInput?.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      const query = searchInput.value.trim();
      if (query.length < 2) {
        if (searchResults) searchResults.innerHTML = '';
        return;
      }
      searchTimer = window.setTimeout(async () => {
        searchController?.abort();
        searchController = new AbortController();
        try {
          const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&section_id=predictive-search`;
          const response = await fetch(url, { signal: searchController.signal });
          if (!response.ok) throw new Error();
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const results = html.querySelector('[data-predictive-search-content]');
          if (searchResults) searchResults.innerHTML = results?.innerHTML || '';
        } catch (error) {
          if (error.name !== 'AbortError' && searchResults) searchResults.innerHTML = '';
        }
      }, 180);
    });
  }

  const revealWithMotion = 'IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealObserver = revealWithMotion
    ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.08 })
    : null;
  const initializeReveal = (container = document) => {
    container.querySelectorAll('[data-reveal]').forEach((item) => {
      if (item.dataset.revealInitialized === 'true') return;
      item.dataset.revealInitialized = 'true';
      if (revealObserver) revealObserver.observe(item);
      else item.classList.add('is-visible');
    });
  };

  initializeReveal();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const updateEditorialVideos = () => {
    document.querySelectorAll('[data-editorial-video] video[autoplay]').forEach((video) => {
      if (reducedMotion.matches) {
        video.pause();
        video.removeAttribute('autoplay');
      }
    });
  };
  updateEditorialVideos();
  reducedMotion.addEventListener?.('change', updateEditorialVideos);

  const refreshCartCount = async () => {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh bag');
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
  };

  const initializeQuickAdd = (container = document) => {
    container.querySelectorAll('[data-quick-add-form]').forEach((form) => {
      if (form.dataset.quickAddInitialized === 'true') return;
      const button = form.querySelector('[data-quick-add]');
      const status = form.querySelector('[data-quick-add-status]');
      if (!button) return;
      form.dataset.quickAddInitialized = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const originalText = button.innerHTML;
        button.disabled = true;
        button.textContent = button.dataset.addingLabel || 'Adding…';
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
          });
          if (!response.ok) throw new Error('This piece could not be added.');
          await refreshCartCount();
          button.textContent = `${button.dataset.addedLabel || 'Added to bag'} ✓`;
          if (status) status.textContent = button.dataset.addedLabel || 'Added to bag';
          window.setTimeout(() => { button.innerHTML = originalText; button.disabled = false; }, 1800);
        } catch (error) {
          button.textContent = button.dataset.errorLabel || 'Try again';
          if (status) status.textContent = button.dataset.errorLabel || error.message;
          window.setTimeout(() => { button.innerHTML = originalText; button.disabled = false; }, 2200);
        }
      });
    });
  };

  initializeQuickAdd();

  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const form = section.querySelector('[data-product-form]');
    const variantsElement = section.querySelector('[data-product-variants]');
    if (!form || !variantsElement) return;

    let variants;
    try {
      variants = JSON.parse(variantsElement.textContent);
    } catch (_error) {
      return;
    }

    const optionSelectors = Array.from(form.querySelectorAll('[data-product-option]'));
    const idInput = form.querySelector('[data-variant-id]');
    const price = section.querySelector('[data-product-price]');
    const comparePrice = section.querySelector('[data-product-compare-price]');
    const button = form.querySelector('[data-add-to-cart]');
    const status = form.querySelector('[data-product-status]');
    const mobileBar = section.querySelector('[data-mobile-product-bar]');
    const mobileButton = section.querySelector('[data-mobile-add]');
    const backInStock = section.querySelector('[data-back-in-stock]');

    const syncOptionButtons = () => {
      optionSelectors.forEach((select, optionIndex) => {
        const group = select.closest('[data-product-option-group]');
        group?.querySelectorAll('[data-option-value]').forEach((optionButton) => {
          optionButton.setAttribute('aria-pressed', String(optionButton.dataset.optionValue === select.value));
          const selectedOptions = optionSelectors.map((optionSelect) => optionSelect.value);
          selectedOptions[optionIndex] = optionButton.dataset.optionValue;
          const possibleVariants = variants.filter((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]));
          const exists = possibleVariants.length > 0;
          const available = possibleVariants.some((candidate) => candidate.available);
          optionButton.disabled = !exists;
          optionButton.toggleAttribute('data-option-sold-out', exists && !available);
        });
      });
    };

    section.querySelectorAll('[data-product-option-group]').forEach((group) => {
      const select = group.querySelector('[data-product-option]');
      if (!select) return;
      group.querySelectorAll('[data-option-value]').forEach((optionButton) => {
        optionButton.addEventListener('click', () => {
          select.value = optionButton.dataset.optionValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });

    section.querySelectorAll('[data-quantity-selector]').forEach((selector) => {
      const input = selector.querySelector('[data-quantity-input]');
      const minus = selector.querySelector('[data-quantity-minus]');
      const plus = selector.querySelector('[data-quantity-plus]');
      if (!input) return;
      minus?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) - 1); });
      plus?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value || 1) + 1); });
    });

    mobileButton?.addEventListener('click', () => button.click());

    const updateProduct = (scrollToMedia = false) => {
      syncOptionButtons();
      const selectedOptions = optionSelectors.map((select) => select.value);
      const variant = optionSelectors.length
        ? variants.find((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]))
        : variants.find((candidate) => String(candidate.id) === idInput.value);

      if (!variant) {
        idInput.value = '';
        button.disabled = true;
        button.textContent = button.dataset.unavailableLabel || 'Unavailable';
        if (mobileButton) {
          mobileButton.disabled = true;
          mobileButton.textContent = button.dataset.unavailableLabel || 'Unavailable';
        }
        status.textContent = button.dataset.unavailableStatus || 'This option combination is unavailable.';
        return;
      }

      idInput.value = variant.id;
      price.textContent = variant.price;
      if (comparePrice) {
        comparePrice.textContent = variant.compare_price || '';
        comparePrice.hidden = !variant.compare_price;
      }
      button.disabled = !variant.available;
      button.textContent = variant.available ? (button.dataset.addLabel || 'Add to bag') : (button.dataset.soldLabel || 'Sold out');
      if (mobileButton) {
        mobileButton.disabled = !variant.available;
        mobileButton.textContent = variant.available ? `${mobileButton.dataset.addLabel || 'Add'} — ${variant.price}` : (mobileButton.dataset.soldLabel || 'Sold out');
      }
      status.textContent = variant.available ? '' : (button.dataset.soldStatus || 'This selection is sold out.');
      if (backInStock) backInStock.hidden = variant.available;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

      section.querySelectorAll('[data-product-media-id]').forEach((media) => media.classList.remove('is-selected'));
      if (variant.featured_media_id) {
        const featuredMedia = section.querySelector(`[data-product-media-id="${variant.featured_media_id}"]`);
        if (featuredMedia) {
          featuredMedia.classList.add('is-selected');
          if (scrollToMedia && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            featuredMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }
    };

    optionSelectors.forEach((select) => select.addEventListener('change', () => updateProduct(true)));
    updateProduct();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (button.disabled) return;
      button.disabled = true;
      button.textContent = button.dataset.addingLabel || 'Adding…';
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error();
        await refreshCartCount();
        button.textContent = button.dataset.addedLabel || 'Added to bag';
        status.textContent = button.dataset.addedLabel || 'Added to bag';
        window.setTimeout(() => { button.textContent = button.dataset.addLabel || 'Add to bag'; button.disabled = false; }, 1800);
      } catch (_error) {
        button.textContent = button.dataset.errorLabel || 'Try again';
        status.textContent = button.dataset.errorLabel || 'This piece could not be added.';
        window.setTimeout(() => { button.textContent = button.dataset.addLabel || 'Add to bag'; button.disabled = false; }, 2200);
      }
    });

    if (mobileBar && 'IntersectionObserver' in window) {
      const submitObserver = new IntersectionObserver(([entry]) => {
        mobileBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      submitObserver.observe(button);
    }
  });

  const mediaDialog = document.querySelector('[data-media-dialog]');
  if (mediaDialog) {
    const dialogContent = mediaDialog.querySelector('[data-media-dialog-content]');
    document.querySelectorAll('[data-media-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        const dialogImage = document.createElement('img');
        dialogImage.src = button.dataset.zoomSrc;
        dialogImage.alt = button.dataset.zoomAlt || '';
        dialogContent.replaceChildren(dialogImage);
        mediaDialog.showModal();
      });
    });
    mediaDialog.querySelector('[data-media-close]')?.addEventListener('click', () => mediaDialog.close());
    mediaDialog.addEventListener('click', (event) => { if (event.target === mediaDialog) mediaDialog.close(); });
  }

  const sizeGuideDialog = document.querySelector('[data-size-guide-dialog]');
  if (sizeGuideDialog) {
    document.querySelector('[data-size-guide-open]')?.addEventListener('click', () => sizeGuideDialog.showModal());
    sizeGuideDialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => sizeGuideDialog.close());
    sizeGuideDialog.addEventListener('click', (event) => { if (event.target === sizeGuideDialog) sizeGuideDialog.close(); });
  }

  const initializeProductRecommendations = (section) => {
    if (!section?.dataset.url || section.innerHTML.trim() || section.dataset.recommendationsInitialized === 'true') return;
    section.dataset.recommendationsInitialized = 'true';

    const loadRecommendations = async () => {
      try {
        const response = await fetch(section.dataset.url);
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendations = html.querySelector('[data-product-recommendations]');
        if (recommendations?.innerHTML.trim()) {
          section.innerHTML = recommendations.innerHTML;
          initializeQuickAdd(section);
          initializeReveal(section);
        }
      } catch (_error) {}
    };

    if (!('IntersectionObserver' in window)) {
      loadRecommendations();
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      loadRecommendations();
    }, { rootMargin: '0px 0px 300px 0px' });
    observer.observe(section);
  };

  document.querySelectorAll('[data-product-recommendations]').forEach(initializeProductRecommendations);
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches('[data-product-recommendations]')
      ? event.target
      : event.target.querySelector('[data-product-recommendations]');
    initializeProductRecommendations(section);
  });

  document.querySelectorAll('[data-recently-viewed]').forEach((section) => {
    const currentElement = section.querySelector('[data-recently-viewed-current]');
    const grid = section.querySelector('[data-recently-viewed-grid]');
    if (!currentElement || !grid) return;
    try {
      const current = JSON.parse(currentElement.textContent);
      const saved = JSON.parse(window.localStorage.getItem('jardRecentlyViewed') || '[]');
      const previous = saved.filter((item) => item.id !== current.id).slice(0, 4);
      previous.forEach((item) => {
        const article = document.createElement('article');
        article.className = 'product-card recently-viewed-card';
        const link = document.createElement('a');
        link.className = 'product-card-media';
        link.href = item.url;
        link.setAttribute('aria-label', item.title);
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.title;
          image.loading = 'lazy';
          image.className = 'product-card-image product-card-image-primary';
          link.append(image);
        }
        const meta = document.createElement('div');
        meta.className = 'product-card-meta';
        const title = document.createElement('h3');
        const titleLink = document.createElement('a');
        titleLink.href = item.url;
        titleLink.textContent = item.title;
        title.append(titleLink);
        const price = document.createElement('div');
        price.className = 'product-card-price';
        price.textContent = item.price;
        meta.append(title, price);
        article.append(link, meta);
        grid.append(article);
      });
      if (previous.length) section.hidden = false;
      window.localStorage.setItem('jardRecentlyViewed', JSON.stringify([current, ...saved.filter((item) => item.id !== current.id)].slice(0, 8)));
    } catch (_error) {}
  });
})();
